import * as DialogPrimitive from "@radix-ui/react-dialog";
import * as React from "react";
// import { useNavigate } from "react-router-dom";
import { Icon, type IconName } from "~/components/icon";
import { cn } from "~/lib/utils";

export interface DialogProps {
  children: React.ReactNode;
  className?: string;
  showDialog?: boolean;
  setShowDialog?: React.Dispatch<React.SetStateAction<boolean>>;
  onClose?: () => void;
  preventDefaultClose?: boolean;
  showClose?: boolean;
  routed?: boolean;
  modal?: boolean;
  sticky?: boolean;
}

const Dialog = ({
  children,
  className,
  showDialog,
  setShowDialog,
  onClose,
  preventDefaultClose,
  showClose = true,
  routed,
  modal = true,
  sticky,
}: DialogProps) => {
  // const navigate = useNavigate();

  const closeDialog = () => {
    if (preventDefaultClose) return;
    onClose && onClose();

    if (setShowDialog) setShowDialog(false);
    // if (routed) navigate(-1);
  };

  return (
    <DialogPrimitive.Root
      modal={modal}
      open={setShowDialog ? showDialog : true}
      onOpenChange={(open) => {
        if (!open) {
          closeDialog();
        }
      }}
    >
      <DialogPrimitive.Portal>
        <DialogPrimitive.Content
          onOpenAutoFocus={(e) => e.preventDefault()}
          onCloseAutoFocus={(e) => e.preventDefault()}
          className={cn(
            "fui-dialog",
            { "fui-dialog--sticky": sticky },
            className,
          )}
        >
          <div className="fui-dialog-body">{children}</div>
          {!preventDefaultClose && showClose && (
            <DialogPrimitive.Close
              className={cn("fui-dialog__close")}
              aria-label="Close"
              onClick={closeDialog}
            >
              <Icon name="x-close" />
            </DialogPrimitive.Close>
          )}
        </DialogPrimitive.Content>
        {modal ? (
          <DialogPrimitive.Overlay className="fui-overlay" />
        ) : (
          <div className="fui-overlay" />
        )}
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
};

const DialogHeader = ({
  className,
  children,
  icon,
  ...props
}: React.HTMLAttributes<HTMLDivElement> & {
  icon?: IconName;
}) => {
  return (
    <div className={cn("fui-dialog__header", className)} {...props}>
      {icon && (
        <span className="bg-primary-200 text-primary flex items-center justify-center w-10 h-10 rounded-4">
          <Icon name={icon} />
        </span>
      )}
      <div className="space-y-2">{children}</div>
    </div>
  );
};

const DialogContent = ({
  className,
  children,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => {
  return (
    <div className={cn("fui-dialog__content", className)} {...props}>
      {children}
    </div>
  );
};

const DialogFooter = ({
  className,
  children,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => {
  return (
    <div className={cn("fui-dialog__footer", className)} {...props}>
      {children}
    </div>
  );
};

const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn("fui-dialog__title", className)}
    {...props}
  />
));

const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn("fui-dialog__description", className)}
    {...props}
  />
));

Dialog.Header = DialogHeader;
Dialog.Content = DialogContent;
Dialog.Footer = DialogFooter;
Dialog.Title = DialogTitle;
Dialog.Description = DialogDescription;

export default Dialog;
